import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { devWarn } from '../../_internal/devWarn';
import { MODAL_SIZE_MD, type ModalSize } from './Modal.constants';
import {
  Backdrop,
  Body,
  CloseButton,
  Footer,
  Header,
  HeaderIcon,
  HeaderText,
  Panel,
  Subtitle,
  TitleEl,
} from './Modal.styled';

export interface ModalProps {
  /** Controls visibility. The portal is unmounted when `open` is false. */
  open: boolean;
  /**
   * Called when the user dismisses the modal — clicking the backdrop, the
   * close button, or pressing Escape.
   */
  onClose: () => void;
  /** Title shown in the header (used as the accessible name). */
  title?: ReactNode;
  /** Optional subtitle shown beneath the title. */
  subtitle?: ReactNode;
  /**
   * Optional 36x36 leading icon rendered before the title block (M3 "dialog
   * icon" pattern). Decorative — wrap your glyph so it has `aria-hidden`.
   */
  icon?: ReactNode;
  /**
   * Skip the tinted square `HeaderIcon` badge and render `icon` bare. Use
   * when the consumer supplies its own fully-styled icon treatment (e.g.
   * `ConfirmDialog`'s tone-tinted disc) so it isn't double-wrapped.
   */
  bareIcon?: boolean;
  /**
   * If you need to render the title yourself (e.g. a custom layout), pass an
   * `aria-label` so the dialog still has an accessible name.
   */
  ariaLabel?: string;
  /** Body content. */
  children?: ReactNode;
  /** Footer slot — typically primary + secondary action buttons. */
  footer?: ReactNode;
  /**
   * Controls the maximum width of the panel. Defaults to `'md'` (560px).
   * `'sm'` 420 / `'md'` 560 / `'lg'` 720 / `'xl'` 960.
   */
  size?: ModalSize;
  /** Hide the close (`×`) button in the header. Defaults to `false`. */
  hideCloseButton?: boolean;
  /** Disable Escape-key dismissal. Defaults to `false`. */
  disableEscape?: boolean;
  /** Disable backdrop-click dismissal. Defaults to `false`. */
  disableBackdropClose?: boolean;
  /** Override the close button's aria-label (default: "Close"). */
  closeAriaLabel?: string;
  /**
   * Optional override for the portal container. Defaults to `document.body`.
   * Useful for tests or for nesting modals inside specific roots.
   */
  container?: HTMLElement | null;
  /** Focused on open in place of the first focusable element. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
  dataTestId?: string;
}

const CloseGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 6 18 18 M18 6 6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Material 3 dialog. Renders into a portal at `document.body` (override via
 * `container`). Dismissable via Escape, backdrop click, or the close button —
 * each of which can be disabled individually.
 *
 * The wrapper traps focus inside the dialog while open and restores focus to
 * the previously-focused element on close. The dialog is announced via
 * `role="dialog"` + `aria-modal="true"`; pass `title` (recommended) or
 * `ariaLabel` to give it an accessible name.
 */
const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  bareIcon = false,
  ariaLabel,
  children,
  footer,
  size = MODAL_SIZE_MD,
  hideCloseButton = false,
  disableEscape = false,
  disableBackdropClose = false,
  closeAriaLabel = 'Close',
  container,
  initialFocusRef,
  className,
  dataTestId = 'modal',
}) => {
  const titleId = useId();
  const subtitleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  if (open && !title && !ariaLabel) {
    devWarn(
      'Modal: rendered without a `title` or `ariaLabel` — screen reader users will hear "dialog" with no name.',
    );
  }

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;

    const focusFirst = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable =
        initialFocusRef?.current ?? panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusable ?? panel).focus();
    };
    const id = window.requestAnimationFrame(focusFirst);

    return () => {
      window.cancelAnimationFrame(id);
      const previous = previouslyFocused.current;
      if (previous && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  }, [open, initialFocusRef]);

  useEffect(() => {
    if (!open || disableEscape) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, disableEscape, handleClose]);

  const trapFocus = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute('aria-hidden'));
    if (focusable.length === 0) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !panel.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableBackdropClose) return;
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const portalTarget = container ?? document.body;
  const labelledBy = title ? titleId : undefined;
  const describedBy = subtitle ? subtitleId : undefined;

  const dialog = (
    <Backdrop
      onMouseDown={onBackdropMouseDown}
      data-testid={`${dataTestId}__backdrop`}
    >
      <Panel
        ref={panelRef}
        $size={size}
        role="dialog"
        aria-modal="true"
        aria-label={!title ? ariaLabel : undefined}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className={className}
        data-testid={dataTestId}
      >
        {!hideCloseButton && (
          <CloseButton
            type="button"
            onClick={handleClose}
            aria-label={closeAriaLabel}
            data-testid={`${dataTestId}__close`}
          >
            <CloseGlyph />
          </CloseButton>
        )}
        {(title || subtitle || icon) && (
          <Header data-testid={`${dataTestId}__header`}>
            {icon &&
              (bareIcon ? (
                icon
              ) : (
                <HeaderIcon
                  aria-hidden="true"
                  data-testid={`${dataTestId}__icon`}
                >
                  {icon}
                </HeaderIcon>
              ))}
            <HeaderText>
              {title && (
                <TitleEl id={titleId} data-testid={`${dataTestId}__title`}>
                  {title}
                </TitleEl>
              )}
              {subtitle && (
                <Subtitle
                  id={subtitleId}
                  data-testid={`${dataTestId}__subtitle`}
                >
                  {subtitle}
                </Subtitle>
              )}
            </HeaderText>
          </Header>
        )}
        <Body data-testid={`${dataTestId}__body`}>{children}</Body>
        {footer && (
          <Footer data-testid={`${dataTestId}__footer`}>{footer}</Footer>
        )}
      </Panel>
    </Backdrop>
  );

  return createPortal(dialog, portalTarget);
};

export default Modal;
